import React, { useState } from "react";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import ReferralRazorPay from "../../components/ReferralRazorPay";

const ReferralSignUp = () => {
  const [searchParams] = useSearchParams();
  const referralCodeFromUrl = searchParams.get("ref");
  const [formData, setFormData] = useState({
    pan: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    referralCode: referralCodeFromUrl || "",
  });
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [userDataForPayment, setUserDataForPayment] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    if (formData.pan.length !== 10) {
      toast.error("PAN must be 10 characters");
      return false;
    }
    if (formData.phone.length !== 10) {
      toast.error("Phone number must be 10 digits");
      return false;
    }
    return true;
  };

  // Helper function to get current AY (Assessment Year)
  const getCurrentAY = () => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-${currentYear + 1}`;
  };

  // Helper function to format date for entryDate
  const getFormattedEntryDate = () => {
    const dateTime = new Date();
    const options = { timeZone: "Asia/Kolkata" };
    const istDate = new Date(dateTime.toLocaleString("en-IN", options));

    const pad = (n) => (n < 10 ? "0" : "") + n;

    const entryDay = pad(istDate.getDate());
    const entryMonth = pad(istDate.getMonth() + 1);
    const entryYear = istDate.getFullYear();
    const entryHours = pad(istDate.getHours() % 12 || 12);
    const entryMinutes = pad(istDate.getMinutes());
    const entrySeconds = pad(istDate.getSeconds());
    const entryAmPm = istDate.getHours() >= 12 ? "PM" : "AM";

    return `${entryDay}-${entryMonth}-${entryYear} ${entryHours}:${entryMinutes}:${entrySeconds} ${entryAmPm}`;
  };

  const handlePaymentSuccess = async (paymentId) => {
    try {
      // Set subscription to expire on March 31st of next year
      const currentYear = new Date().getFullYear();
      const nextYearMarch31 = new Date(currentYear + 1, 2, 31);

      await updateDoc(doc(db, "usersTable", formData.pan.toUpperCase()), {
        subscriptionExpiry: nextYearMarch31.toISOString(),
        lastPaymentId: paymentId,
        accountActivated: true,
      });

      toast.success("Payment successful! Account activated.");
      
      // Navigate to dashboard after successful payment
      setTimeout(() => {
        navigate("/", { state: { from: { pathname: "/dashboard" } } });
      }, 1500);
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.error("Payment successful but there was an error updating your records. Please contact support.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Check if PAN already exists
      const panDoc = await getDoc(doc(db, "usersTable", formData.pan.toUpperCase()));
      if (panDoc.exists()) {
        toast.error("PAN already registered");
        setLoading(false);
        return;
      }

      // Check if email already exists
      const emailQuery = query(collection(db, "usersTable"), where("email", "==", formData.email));
      const emailSnapshot = await getDocs(emailQuery);
      if (!emailSnapshot.empty) {
        toast.error("Email already registered");
        setLoading(false);
        return;
      }

      const formatEmail = `${formData.email.split("@")[0]}+${formData.pan.toLowerCase()}@${formData.email.split("@")[1]}`;

      // Create user in Firebase Auth with the original email (not formatted)
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formatEmail,
        formData.password
      );

      // Update user profile
      await updateProfile(userCredential.user, {
        displayName: `${formData.firstName} ${formData.lastName}`
      });

      // Get current AY and formatted date
      const currentAY = getCurrentAY();
      const entryDate = getFormattedEntryDate();

      // Create user document in Firestore with the same structure as page.tsx
      const userData = {
        name: `${formData.firstName} ${formData.lastName}`,
        pan: formData.pan.toUpperCase(),
        mobile: formData.phone,
        email: formData.email,
        emailPAN: formatEmail, // Using the same email as entered
        password: formData.password,
        userType: "taxpayer",
        itrType: "NIL", // Default value
        services: ["itrBasic"], // Default service
        city: "",
        area: "",
        invNo: 0, // Will be updated later if needed
        [currentAY]: {
          Fees: 0,
          FinalFees: 0,
          PaidFees: 0,
          PendingFees: 0,
          dateOfPayment: "01-01-1970", // Default date
          dateOfEntry: entryDate,
          Remark: "",
          discount: 0,
          clientEdit: true,
        },
        aadhaarNo: 0,
        bankAccountNo: 0,
        ifscCode: "",
        employeeId: "",
        officeAddress: "",
        entryMonth: "",
        accountActivated: false, // Set to false initially, will be activated after payment
        referralCode: formData.referralCode,
        createdAt: new Date().toISOString(),
      };

      await setDoc(doc(db, "usersTable", formData.pan.toUpperCase()), userData);

      // Create itrWorking document with the same structure as page.tsx
      const itrWorking = {
        pan: formData.pan.toUpperCase(),
        [currentAY]: {
          incomeFromSalaries: {
            0: {
              address: "",
              allowance: {
                conveyance: 0,
                edu: 0,
                hra: 0,
                other: 0,
                travel: 0,
                uniform: 0,
              },
              city: "",
              designation: "",
              empPAN: "",
              employerName: "",
              natureOfEmployment: "",
              pincode: 0,
              salary: 0,
              salaryType: "Salaries, Allowances, and Perquisites",
              state: "",
              tan: "",
            },
            1: {
              address: "",
              allowance: {
                conveyance: 0,
                edu: 0,
                hra: 0,
                other: 0,
                travel: 0,
                uniform: 0,
              },
              city: "",
              designation: "",
              empPAN: "",
              employerName: "",
              natureOfEmployment: "",
              pincode: 0,
              salary: 0,
              salaryType: "Pension",
              state: "",
              tan: "",
            },
          },
          incomeFromOtherSources: {
            dividends: 0,
            interestIncome: {
              InterestOnSavingsAccount: 0,
              InterestOnTimeDeposits: 0,
              otherInterest: 0,
            },
            winnings: {
              winningsFromOnlineGamesTaxableUS115BBJ: 0,
              winningsTaxableUS115BB: 0,
            },
          },
          incomeFromHouseProperty: {
            LetOut: {
              grossAnnual: 0,
              interestOnBorrowedCapital: 0,
              municipalTaxes: 0,
            },
            SelfOccupied: {
              interestOnBorrowedCapital: 0,
            },
          },
          deductions: {
            "80C": {
              gpf: 0,
              homeLoan: 0,
              lic: 0,
              tuitionFee: 0,
            },
            "80CCD": {
              "80CCD-1": 0,
              "80CCD-1B": 0,
              "80CCD-2": 0,
            },
            "80D": {
              above60: 0,
              below60: 0,
            },
            "80DD": 0,
            "80DDB": {
              amount: 0,
              userType: "",
            },
            "80G": 0,
            "80GG": 0,
            "80TTA": 0,
            "80U": 0,
          },
          tax: {
            newRegime: {
              taxWithoutCess: 0,
            },
            oldRegime: {
              taxWithoutCess: 0,
            },
          },
          suggestions: {
            0: false,
            1: false,
            2: false,
            3: false,
            4: true,
            5: true,
            6: true,
            7: true,
            8: true,
            9: true,
            10: true,
            "$80ddbRelation": false,
          }
        }
      };

      await setDoc(doc(collection(db, "itrWorking"), formData.pan.toUpperCase()), itrWorking);

      // Create EnquirySheet document
      const enquirySheetData = {
        pan: formData.pan.toUpperCase(),
        [currentAY]: {
          clientStatus: null,
          closedBy: null,
          assignedTo: null,
          closedFor: null,
          Status: null,
          Remark: null,
          district: "",
        },
      };

      await setDoc(doc(collection(db, "EnquirySheet"), formData.pan.toUpperCase()), enquirySheetData);

      // Create tdsSheet document
      const tdsSheetData = {
        pan: formData.pan.toUpperCase(),
        [currentAY]: {
          computationITA: null,
          consultedBy: null,
          deductionDetails: null,
          incomeDetails: null,
          managedBy: null,
          packageCall: null,
          packageClosure: null,
          remarks: null,
          taxDetails: null,
        },
      };

      await setDoc(doc(collection(db, "tdsSheet"), formData.pan.toUpperCase()), tdsSheetData);

      // Credit the referrer (only if referral code is provided and not QWERTY9876)
      if (formData.referralCode && formData.referralCode !== "QWERTY9876") {
        try {
          const referrerDoc = await getDoc(doc(db, "usersTable", formData.referralCode));
          if (referrerDoc.exists()) {
            const referrerData = referrerDoc.data();
            const updatedReferrals = [...(referrerData.referrals || []), {
              referredPan: formData.pan.toUpperCase(),
              date: new Date().toISOString(),
              status: "pending" // Set to pending until payment is completed
            }];

            await updateDoc(doc(db, "usersTable", formData.referralCode), {
              referrals: updatedReferrals
            });
          }
        } catch (error) {
          console.error("Error updating referrer:", error);
          // Don't show error to user, just log it
        }
      }

      // If referral code is QWERTY9876, activate account and redirect to dashboard
      if (formData.referralCode === "QWERTY9876") {
        await updateDoc(doc(db, "usersTable", formData.pan.toUpperCase()), {
          accountActivated: true,
        });
        toast.success("Account created and activated successfully!");
        setTimeout(() => {
          navigate("/", { state: { from: { pathname: "/dashboard" } } });
        }, 1500);
        setLoading(false);
        return;
      }

      toast.success("Account created successfully! Please complete payment to activate your account.");
      
      // Show payment modal instead of redirecting
      setUserDataForPayment({
        userEmail: formData.email,
        userName: `${formData.firstName} ${formData.lastName}`,
        pan: formData.pan.toUpperCase()
      });
      setShowPayment(true);
      setLoading(false);

    } catch (error) {
      console.error("Signup error:", error);
      toast.error(error.message);
      setLoading(false);
    }
  };

  if (showPayment && userDataForPayment) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body text-center">
                <h3 className="card-title">Complete Your Registration</h3>
                <p className="text-muted">
                  Please complete the payment to activate your account and access all features.
                </p>
                
                <ReferralRazorPay
                  userEmail={userDataForPayment.userEmail}
                  userName={userDataForPayment.userName}
                  onSuccess={handlePaymentSuccess}
                  buttonText="Pay Now & Activate Account"
                  amount={299} // ₹1000.00 (100000 paise)
                />
                
                <button
                  className="btn btn-outline-secondary mt-3"
                  onClick={() => {
                    // User can choose to pay later, but account won't be activated
                    toast.info("You can complete payment later from your dashboard");
                    navigate("/", { state: { from: { pathname: "/dashboard" } } });
                  }}
                >
                  Pay Later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h3 className="card-title text-center">Create Account</h3>
              {(formData.referralCode || referralCodeFromUrl) && (
                <p className="text-center text-muted">
                  Signing up with referral code: {formData.referralCode || referralCodeFromUrl}
                </p>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>PAN Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    placeholder="e.g., ABCDE1234F"
                    required
                    maxLength={10}
                  />
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    maxLength={10}
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    className="form-control"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Referral Code (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="referralCode"
                    value={formData.referralCode}
                    onChange={handleChange}
                    placeholder="Enter referral code if any"
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-block"
                  disabled={loading}
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralSignUp;